import { NavigateFunction } from "react-router-dom";
import AppRoutes from "../routes/AppRoutes";
import {
  UserContextInterface,
  UserContextIsPopulated,
} from "../state/UserContext";
import { AdminModule } from "../types/admin/client-journeys/AdminModule";
import AdminDashboardItem from "../types/admin/dashboard/AdminDashboardItem";
import { getAdminDashboardItems } from "../components/admin/dashboard/adminDashboardItems";
import permissionHelper from "./permissionHelper";
import moduleAccessHelper from "./moduleAccessHelper";
import { ClientPermissionMode } from "../types/access/ClientPermissionMode";

export const adminHelper = {
  // Checks if the user has admin access and if not redirects them to the dashboard.
  checkAdminAccessAndRedirectWhenNoAccess(
    userContext: UserContextInterface,
    module: AdminModule,
    nav: NavigateFunction,
  ): void {
    if (UserContextIsPopulated(userContext.user)) {
      if (!this.doesUserHaveAdminModuleAccess(userContext, module)) {
        nav(AppRoutes.yourJourney.root);
      }
    }
  },

  // Checks if the user has admin access and returns true/false accordingly.
  checkUserHasAdminAccessToModule(
    userContext: UserContextInterface,
    module: AdminModule,
  ): boolean {
    if (UserContextIsPopulated(userContext.user)) {
      if (!this.doesUserHaveAdminModuleAccess(userContext, module)) {
        return false;
      }
    }
    return true;
  },

  doesUserHaveAnyAdminModuleAccess(userContext: UserContextInterface): boolean {
    const enabledAdminModules = this.getEnabledAdminModulesForUser(userContext);
    return enabledAdminModules.length > 0;
  },

  doesUserHaveAdminModuleAccess(
    userContext: UserContextInterface,
    module: AdminModule,
  ): boolean {
    const enabledAdminModules = this.getEnabledAdminModulesForUser(userContext);
    return enabledAdminModules.includes(module);
  },

  // Ideally this needs to be moved onto the userContext (or something similar like SignedInUserDto), that way it only
  // needs to work these out once on log in... rather than on the fly all the time.
  getEnabledAdminModulesForUser(
    userContext: UserContextInterface,
  ): AdminModule[] {
    const adminModules: AdminModule[] = [];
    const managerAdminsTheirPeopleConfig =
      userContext.user.client.moduleConfigs.find((config) => {
        return config.key === "ManagersAdministerTheirEmployees";
      });

    // Give access to the user-journey tab (and corresponding pages) if the user is an admin, or if they
    // are a manager with the 'ManagerAdminsTheirPeople' config.
    if (
      userContext.user.isAdmin ||
      (managerAdminsTheirPeopleConfig != undefined &&
        managerAdminsTheirPeopleConfig.value === "true" &&
        userContext.user.isManager)
    ) {
      adminModules.push("USER-JOURNEY");
    }

    // Give access to the journey-config tab if the user has either: 1) Legacy 'JourneyAdministration' permission,
    // or 2) Access-based 'JOURNEY-ADMIN' permission.
    if (
      userContext.user.isAdmin &&
      permissionHelper.userHasEitherPermission(
        userContext,
        "JourneyAdministration",
        "JOURNEY-ADMIN",
      )
    ) {
      adminModules.push("JOURNEY-CONFIG");
    }

    return adminModules;
  },

  getDashboardItems(
    permissionMode: ClientPermissionMode,
  ): AdminDashboardItem[] {
    return getAdminDashboardItems(permissionMode);
  },

  /**
   * True when the admin home dashboard would show at least one card (e.g. external score
   * uploader lives only on the dashboard, not in admin left nav).
   */
  hasVisibleAdminDashboardCards(userContext: UserContextInterface): boolean {
    const items = this.filterDashboardItems(
      this.getDashboardItems(userContext.user.client.permissionMode),
      userContext,
    );
    return items.length > 0;
  },

  filterDashboardItems(
    items: AdminDashboardItem[],
    userContext: UserContextInterface,
  ): AdminDashboardItem[] {
    return items.filter((item) => {
      const hasPermissions =
        !item.allowedPermissions ||
        item.allowedPermissions.some((permission) =>
          permissionHelper.userHasPermission(userContext, permission),
        );

      const hasAdminModule =
        !item.requiredModule ||
        this.doesUserHaveAdminModuleAccess(userContext, item.requiredModule);

      const hasClientModule =
        !item.requiredClientModule ||
        moduleAccessHelper.userHasModule(
          userContext.user,
          item.requiredClientModule,
        );

      return hasPermissions && hasAdminModule && hasClientModule;
    });
  },
};

export default adminHelper;
